<script setup lang="ts">
import merge from 'lodash-es/merge';
import { useWebsitesStore } from '#layers/cms/app/pages/cms/management/websites/_runtime/stores/websites';
import type { PageListItemModel } from '#layers/cms/app/pages/cms/management/pages/_runtime/services/PagesService';
import PagesService from '#layers/cms/app/pages/cms/management/pages/_runtime/services/PagesService';
import Multiselect from 'vue-multiselect/src/Multiselect.vue';
import * as yup from 'yup';

interface FormSettings
{
    settings: any;
}

defineOptions({
    name: 'WidgetNewestEntriesSettings'
});

const emit = defineEmits<{
    (e: 'update:modelValue', payload: any): void;
}>();

const props = defineProps<{
    modelValue: any;
}>();

const defaults = ref<any>({
    display: '10',
    articlesPages: [],
    announcementsPages: [],
    galleryPages: []
});

const articlesPageOptions = ref<PageListItemModel[]>([]);
const announcementsPageOptions = ref<PageListItemModel[]>([]);
const galletyPageOptions = ref<PageListItemModel[]>([]);

const form = computed<FormType<FormSettings>>(() =>
{
    return props.modelValue;
});

const current = computed((): CurrentWebsite =>
{
    return useWebsitesStore().current;
});

const displayOptions = computed((): Option[] =>
{
    return [
        { value: '3', text: $t('[[[3]]]') },
        { value: '5', text: $t('[[[5]]]') },
        { value: '10', text: $t('[[[10]]]') }
    ];
});

const articlesSelectedPageOptions = computed<Option[]>(() =>
{
    return articlesPageOptions.value.map(p =>
    {
        return { value: p.id.toString(), text: p.option };
    });
});

const announcementsSelectedPageOptions = computed<Option[]>(() =>
{
    return announcementsPageOptions.value.map(p =>
    {
        return { value: p.id.toString(), text: p.option };
    });
});

const gallerySelectedPageOptions = computed<Option[]>(() =>
{
    return galletyPageOptions.value.map(p =>
    {
        return { value: p.id.toString(), text: p.option };
    });
});

async function loadArticlesPages(): Promise<void>
{
    try
    {
        articlesPageOptions.value = await PagesService.getPagesByModule(current.value.website, current.value.locale, 'articles');
        articlesPageOptions.value = articlesPageOptions.value.filter(x => x.id != 0);
    }
    catch (ex)
    {
        articlesPageOptions.value = [];
        $log.debug(ex);
    }
}

async function loadAnnouncementsPages(): Promise<void>
{
    try
    {
        announcementsPageOptions.value = await PagesService.getPagesByModule(current.value.website, current.value.locale, 'int_announcements');
        announcementsPageOptions.value = announcementsPageOptions.value.filter(x => x.id != 0);
    }
    catch (ex)
    {
        announcementsPageOptions.value = [];
        $log.debug(ex);
    }
}

async function loadGalleryPages(): Promise<void>
{
    try
    {
        const response = await PagesService.getPagesByModule(current.value.website, current.value.locale, 'gallery');

        galletyPageOptions.value = [...response.filter(x => x.id != 0)];
    }
    catch (ex)
    {
        $log.debug(ex);
    }
}

async function loadIntranetGalleryPages(): Promise<void>
{
    try
    {
        const response = await PagesService.getPagesByModule(current.value.website, current.value.locale, 'int_gallery');

        galletyPageOptions.value = [...response.filter(x => x.id != 0), ...galletyPageOptions.value];
    }
    catch (ex)
    {
        $log.debug(ex);
    }
}

async function validate(): Promise<void>
{
}

const triggetModel = (): void =>
{
    emit('update:modelValue', form.value);
};

watch(() => form.value.settings, (value: any) =>
{
    triggetModel();
}, { deep: true });

onBeforeMount(async (): Promise<void> =>
{
    await wait(() => current.value.website);
    await loadArticlesPages();
    await loadAnnouncementsPages();
    await loadGalleryPages();
    await loadIntranetGalleryPages();
    form.value.settings = merge(defaults.value, form.value.settings);
});

defineExpose({
    validate
});
</script>

<template>
    <IdeoFormGroup :label="$t('[[[Wybór listy stron artykułów]]]')" :invalid-feedback="form.$errors.first('settings.articlesPages')" :state="form.$errors.state('settings.pages')">
        <Multiselect
            v-model="form.settings.articlesPages"
            label="text"
            track-by="value"
            :options="articlesSelectedPageOptions"
            :multiple="true"
            :placeholder="$t('[[[Wybierz strony]]]')"
            :select-label="$t('[[[Wybierz]]]')"
            :selected-label="$t('[[[Wybrane]]]')"
            :deselect-label="$t('[[[Usuń]]]')"
        >
            <template #noOptions>
                {{ $t('[[[Lista jest pusta]]]') }}
            </template>
            <template #noResult>
                {{ $t('[[[Nie znaleziono żadnych wyników.]]]') }}
            </template>
        </Multiselect>
    </IdeoFormGroup>
    <IdeoFormGroup :label="$t('[[[Wybór listy stron ogłoszeń]]]')" :invalid-feedback="form.$errors.first('settings.announcementsPages')" :state="form.$errors.state('settings.pages')">
        <Multiselect
            v-model="form.settings.announcementsPages"
            label="text"
            track-by="value"
            :options="announcementsSelectedPageOptions"
            :multiple="true"
            :placeholder="$t('[[[Wybierz strony]]]')"
            :select-label="$t('[[[Wybierz]]]')"
            :selected-label="$t('[[[Wybrane]]]')"
            :deselect-label="$t('[[[Usuń]]]')"
        >
            <template #noOptions>
                {{ $t('[[[Lista jest pusta]]]') }}
            </template>
            <template #noResult>
                {{ $t('[[[Nie znaleziono żadnych wyników.]]]') }}
            </template>
        </Multiselect>
    </IdeoFormGroup>
    <IdeoFormGroup :label="$t('[[[Wybór listy stron galerii]]]')" :invalid-feedback="form.$errors.first('settings.galleryPages')" :state="form.$errors.state('settings.pages')">
        <Multiselect
            v-model="form.settings.galleryPages"
            label="text"
            track-by="value"
            :options="gallerySelectedPageOptions"
            :multiple="true"
            :placeholder="$t('[[[Wybierz strony]]]')"
            :select-label="$t('[[[Wybierz]]]')"
            :selected-label="$t('[[[Wybrane]]]')"
            :deselect-label="$t('[[[Usuń]]]')"
        >
            <template #noOptions>
                {{ $t('[[[Lista jest pusta]]]') }}
            </template>
            <template #noResult>
                {{ $t('[[[Nie znaleziono żadnych wyników.]]]') }}
            </template>
        </Multiselect>
    </IdeoFormGroup>
    <IdeoFormGroup :label="$t('[[[Liczba wyświetlanych najnowszych elementów]]]')" :invalid-feedback="form.$errors.first('settings.display')" :state="form.$errors.state('settings.display')">
        <IdeoFormSelect v-model="form.settings.display" :options="displayOptions" />
    </IdeoFormGroup>
</template>
